import { Trans, msg } from '@lingui/macro';
import { Redirect, Route, Switch, useParams } from 'react-router-dom';
import { Box, Flex, Text } from '~/design-system';
import {
  ProgressIndicator as BaseProgressIndicator,
  ProgressStep,
} from '../../../../components/Layout/ProgressIndicator';
import { getStepLinks } from '../../../_config';
import NotFound from '../../404';
import { useCommonLinks } from '../../_config';
import useImportFlow from '../useImportFlow';
import Complete from './complete';
import _bottlebooksFieldsSource from './fields/bottlebooksFieldsSource.json';
import { elabelFields } from './fields/elabelFieldsSource';
import IdentifierNewProductsValidation from './identifierNewProductsValidation';
import IdentifierValidation from './identifierValidation';
import IdentifyFields from './identifyFields';
import IdentifyNewProductsFields from './identifyNewProductsFields';
import MapFields from './mapFields';
import MapHeader from './mapHeader';
import MapValues from './mapValues';
import Name from './name';
import Ready from './ready';
import Suppliers from './suppliers/_config';
import Validation from './validation';

export const bottlebooksFieldsSource = [
  ..._bottlebooksFieldsSource,
  ...elabelFields,
];

export function $ImportIdRoutes({ match }) {
  return (
    <Switch>
      <Route path={`${match.path}/name`} component={Name} />
      <Route path={`${match.path}/mapHeader`} component={MapHeader} />
      <Route
        path={`${match.path}/identifyNewProductsFields`}
        component={IdentifyNewProductsFields}
      />
      <Route path={`${match.path}/identifyFields`} component={IdentifyFields} />
      <Route
        path={`${match.path}/identifierNewProductsValidation`}
        component={IdentifierNewProductsValidation}
      />
      <Route
        path={`${match.path}/identifierValidation`}
        component={IdentifierValidation}
      />
      <Route path={`${match.path}/suppliers`} component={Suppliers} />
      <Route path={`${match.path}/mapFields`} component={MapFields} />
      <Route path={`${match.path}/validation`} component={Validation} />
      <Route path={`${match.path}/mapValues`} component={MapValues} />
      <Route path={`${match.path}/ready`} component={Ready} />
      <Route path={`${match.path}/complete`} component={Complete} />
      {/* The starting route: */}
      <Redirect exact from={`${match.path}`} to={`${match.url}/mapHeader`} />
      <Route component={NotFound} />
    </Switch>
  );
}

export const importType = 'productAllInOneImport';

/** @typedef {keyof ReturnType<getProductDataImportLinks>} Step The possible steps for productDataImport. */

/**
 * @param {object} params
 * @param {string} params.workspaceId
 * @param {string} params.importId
 * @returns
 */
export function useGetProductDataImportLinks({ workspaceId, importId }) {
  const common = useCommonLinks();
  const baseLink = `/${workspaceId}`;
  const baseImportId = importId;
  return {
    root: common.root,
    uploadFile: () => `${baseLink}/${importType}/uploadFile`,
    /**
     * We might not have set the importId yet if we've just uploaded a new file. We may pass an importId in that case.
     * @param {{ importId?: string}} [options]
     */
    name: (options) =>
      `${baseLink}/${importType}/${options?.importId || baseImportId}/name${
        window.location.search
      }`,
    mapHeader: () => `${baseLink}/${importType}/${importId}/mapHeader`,
    identifyFields: () =>
      `${baseLink}/${importType}/${importId}/identifyFields`,
    identifierValidation: () =>
      `${baseLink}/${importType}/${importId}/identifierValidation`,
    identifyNewProductsFields: () =>
      `${baseLink}/${importType}/${importId}/identifyNewProductsFields`,
    suppliers: ({ tab } = { tab: 'all' }) =>
      `${baseLink}/${importType}/${importId}/suppliers/${tab}`,
    supplier: (options) => {
      return `${baseLink}/${importType}/${importId}/suppliers/${options?.supplierName}`;
    },
    createCompany: ({ supplierName, companyData }) => {
      return `${baseLink}/${importType}/${importId}/suppliers/add/${supplierName}?${serializeQuery(
        companyData
      )}`;
    },
    searchCompany: ({ supplierName, companyData }) => {
      return `${baseLink}/${importType}/${importId}/suppliers/add/${supplierName}/search?${serializeQuery(
        companyData
      )}`;
    },
    identifierNewProductsValidation: () =>
      `${baseLink}/${importType}/${importId}/identifierNewProductsValidation`,
    mapFields: () => `${baseLink}/${importType}/${importId}/mapFields`,
    mapValues: () => `${baseLink}/${importType}/${importId}/mapValues`,
    validation: () => `${baseLink}/${importType}/${importId}/validation`,
    ready: () => `${baseLink}/${importType}/${importId}/ready`,
    complete: () => `${baseLink}/${importType}/${importId}/complete`,
  };
}

function serializeQuery(obj) {
  var str = [];
  for (var p in obj)
    if (obj.hasOwnProperty(p)) {
      str.push(encodeURIComponent(p) + '=' + encodeURIComponent(obj[p]));
    }
  return str.join('&');
}

export function ProgressIndicator({ step }) {
  /** @type {{importId: string}} */
  const { importId } = useParams();
  const { isAddingNewProducts, isProducer, loading } = useImportFlow();
  if (loading) return null;
  const updateSteps = ['uploadFile', 'identifyFields', 'mapData', 'ready'];
  const createAndUpdateSteps = [
    'uploadFile',
    'identifyFields',
    'matchSuppliers',
    'validate',
    'mapData',
    'ready',
  ];
  const steps = isAddingNewProducts ? createAndUpdateSteps : updateSteps;
  const currentIndex = steps.indexOf(step);
  const currentStep = currentIndex + 1;

  return (
    <Flex sx={{ justifyContent: 'space-between', paddingX: 40 }}>
      <Box sx={{ width: 150, flexShrink: 0, textAlign: 'left' }}>
        <Trans>Product Updates</Trans>
      </Box>
      <BaseProgressIndicator currentIndex={currentIndex}>
        <ProgressStep>
          <Trans>Upload file</Trans>
        </ProgressStep>
        <ProgressStep>
          <Trans>Identify data</Trans>
        </ProgressStep>
        {isAddingNewProducts && !isProducer && (
          <ProgressStep>
            <Trans>Match producers</Trans>
          </ProgressStep>
        )}
        {isAddingNewProducts && (
          <ProgressStep>
            <Trans>Validate</Trans>
          </ProgressStep>
        )}
        <ProgressStep>
          <Trans>Map data</Trans>
        </ProgressStep>
        <ProgressStep>
          <Trans>Confirm</Trans>
        </ProgressStep>
      </BaseProgressIndicator>
      <Box sx={{ width: 150, flexShrink: 0, textAlign: 'right' }}>
        <Text>
          Step {currentStep} of {steps.length}
        </Text>
      </Box>
    </Flex>
  );
}

/**
 * @param {Step} currentStep
 * @param {boolean} newProductsFlow
 * @returns
 */
export function useLink(currentStep) {
  /** @type {{importId: string}} */
  const { workspaceId, importId } = useParams();
  const { isAddingNewProducts, isProducer, loading } = useImportFlow();
  const urls = useGetProductDataImportLinks({ workspaceId, importId });

  if (!isAddingNewProducts && !loading) {
    delete urls.identifyNewProductsFields;
    delete urls.identifierNewProductsValidation;
    delete urls.suppliers;
    delete urls.supplier;
    delete urls.createCompany;
    delete urls.searchCompany;
  }
  if (isProducer && !loading) {
    delete urls.suppliers;
    delete urls.supplier;
    delete urls.createCompany;
    delete urls.searchCompany;
  }

  return getStepLinks(
    urls,
    // Filter substeps.
    Object.keys(urls).filter(
      (key) => !['supplier', 'createCompany', 'searchCompany'].includes(key)
    ),
    currentStep
  );
}

export const fieldsToImport = [
  // "instanceId" or "productExternalId", one of them must be filtered. Check wich one is not present is identifierFields.
  // { key: 'product.instanceId', label: 'Product Unique ID', required: true },
  {
    key: 'product.productExternalId',
    label: msg`Product External ID`,
    required: true,
  },
];

export const newProductsFieldsToImport = [
  //"instanceId" / "productExternalId" must be in this step also, otherwise it will be overwritten in identifierFields map.
  //one of them must be filtered. Check wich one is not present is identifierFields.
  {
    key: 'product.instanceId',
    label: msg`Bottlebooks Product ID`,
    required: true,
  },
  {
    key: 'product.productExternalId',
    label: msg`Product External ID`,
    required: true,
  },
  { key: 'product.shortName', label: msg`Product short name`, required: true },
  { key: 'producer.name', label: msg`Producer name`, required: true },
  {
    label: msg`Product vintage`,
    key: 'product.vintage',
    required: false,
  },
  // {
  //   label: 'Product internal ID',
  //   key: 'product.inventoryId',
  //   required: false,
  // },
];
